import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import {
    FileText,
    Camera,
    Receipt,
    Plus,
    ChevronLeft,
    ChevronRight,
    Check,
    Upload,
    X,
    Calendar,
    Clock,
    Users,
    MapPin,
    Euro,
    FileImage,
    Trash2,
    Eye,
    CheckCircle,
    XCircle,
    MessageSquare,
    Download,
    Link,
    Copy,
    QrCode,
    Search,
    Building2,
    PenTool,
    Edit3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SocialFacePicker from '../components/SocialFacePicker';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const EXPENSE_CATEGORIES = [
    { value: 'Fahrtkosten', label: 'Fahrtkosten', icon: '🚗' },
    { value: 'Verpflegung', label: 'Verpflegung', icon: '🍽️' },
    { value: 'Unterkunft', label: 'Unterkunft', icon: '🏨' },
    { value: 'Equipment', label: 'Equipment', icon: '📷' },
    { value: 'Sonstiges', label: 'Sonstiges', icon: '📦' }
];

const FormsPage = () => {
    const { api, user } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [shootingDocs, setShootingDocs] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [einverstaendnisse, setEinverstaendnisse] = useState([]);
    const [clientIntakes, setClientIntakes] = useState([]);
    const [extraServices, setExtraServices] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Wizard states
    const [showWizard, setShowWizard] = useState(false);
    const [wizardType, setWizardType] = useState(null); // 'shooting' or 'expense'
    const [currentStep, setCurrentStep] = useState(0);
    
    // View modal
    const [viewItem, setViewItem] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    
    // QR Code Modals
    const [showQrModal, setShowQrModal] = useState(false);
    
    // Extra Services
    const [showExtraModal, setShowExtraModal] = useState(false);
    const [extraForm, setExtraForm] = useState({
        customer_id: '', customer_name_manual: '', service_title: '', description: '',
        notes: '', service_date: new Date().toISOString().slice(0, 10),
        month: new Date().toISOString().slice(0, 7), attachments: []
    });
    const [extraUploading, setExtraUploading] = useState(false);
    const [extraFilter, setExtraFilter] = useState('__all__');
    const [showIntakeQrModal, setShowIntakeQrModal] = useState(false);
    
    // Filters
    const [companyFilter, setCompanyFilter] = useState('');
    const [intakeFilter, setIntakeFilter] = useState('');
    
    // Check if user is admin or account_manager
    const isAdmin = user?.role === 'chef' || user?.role === 'admin';
    const isAccountManager = user?.role === 'account_manager';
    const canViewEinverstaendnis = isAdmin || isAccountManager;
    const canApprove = isAdmin || user?.permissions?.forms_approve;
    const canViewAll = isAdmin || user?.permissions?.forms_view_all;
    
    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [shootingRes, expensesRes, customersRes, einverstaendnisRes, intakeRes, extraRes] = await Promise.all([
                api.get('/forms/shooting'),
                api.get('/forms/expenses'),
                api.get('/customers'),
                canViewEinverstaendnis ? api.get('/shooting-einverstaendnis') : Promise.resolve({ data: [] }),
                canViewEinverstaendnis ? api.get('/client-intake') : Promise.resolve({ data: [] }),
                api.get('/extra-services')
            ]);
            setShootingDocs(shootingRes.data);
            setExpenses(expensesRes.data);
            setCustomers(customersRes.data || []);
            setEinverstaendnisse(einverstaendnisRes.data || []);
            setClientIntakes(intakeRes.data || []);
            setExtraServices(extraRes.data || []);
        } catch (error) {
            console.error('Error fetching forms data:', error);
        } finally {
            setLoading(false);
        }
    }, [api, canViewEinverstaendnis]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    const [wizardPrefill, setWizardPrefill] = useState(null);

    const openWizard = (type) => {
        setWizardType(type);
        setCurrentStep(0);
        setShowWizard(true);
    };
    
    const closeWizard = () => {
        setShowWizard(false);
        setWizardType(null);
        setCurrentStep(0);
        setWizardPrefill(null);
    };

    // Aus dem Kalender geoeffnet: Doku vorausgefuellt starten oder bestehende Doku anzeigen
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const eventId = params.get('shooting_event');
        if (!eventId) return;
        // Alter Link aus dem Kalender: Abschluss laeuft jetzt direkt im Kalender
        window.location.href = `/calendar?complete_event=${eventId}`;
    }, [api]);
    
    const handleWizardComplete = () => {
        closeWizard();
        fetchData();
    };
    
    const openViewModal = (item, type) => {
        setViewItem({ ...item, type });
        setShowViewModal(true);
    };
    
    const handleStatusUpdate = async (expenseId, status, comment = '') => {
        try {
            await api.put(`/forms/expenses/${expenseId}/status`, {
                status,
                admin_comment: comment
            });
            toast.success(status === 'approved' ? 'Genehmigt' : 'Abgelehnt');
            fetchData();
            setShowViewModal(false);
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-5xl mx-auto p-4 md:p-6 lg:p-8 space-y-6 pb-20">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Formulare</h1>
                    <p className="text-muted-foreground text-sm">Shooting-Dokumentationen & Spesenabrechnungen</p>
                </div>
            </div>
            
            {/* Form Selection Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { toast.info('Shootings werden jetzt direkt im Kalender abgeschlossen: Termin öffnen › "Shooting abschließen". Die Doku entsteht dabei automatisch.', { duration: 8000 }); window.location.href = '/calendar'; }}
                    className="p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/10 border border-primary/20 hover:border-primary/40 transition-all text-left"
                    data-testid="shooting-doc-redirect"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
                            <Camera className="w-7 h-7 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">Shooting abschließen</h3>
                            <p className="text-sm text-muted-foreground">Läuft jetzt über den Kalendertermin</p>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-primary">
                        <Plus className="w-4 h-4" />
                        <span className="text-sm font-medium">Neues Formular</span>
                    </div>
                </motion.button>
                
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => openWizard('expense')}
                    className="p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/10 border border-primary/20 hover:border-primary/40 transition-all text-left"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
                            <Receipt className="w-7 h-7 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">Spesen</h3>
                            <p className="text-sm text-muted-foreground">Ausgaben einreichen</p>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-primary">
                        <Plus className="w-4 h-4" />
                        <span className="text-sm font-medium">Neue Abrechnung</span>
                    </div>
                </motion.button>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowExtraModal(true)}
                    className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 hover:border-amber-500/40 transition-all text-left"
                    data-testid="extra-service-card"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center">
                            <Euro className="w-7 h-7 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">Extra-Leistung</h3>
                            <p className="text-sm text-muted-foreground">Zusaetzliche Leistung erfassen</p>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-amber-600">
                        <Plus className="w-4 h-4" />
                        <span className="text-sm font-medium">Neue Leistung</span>
                    </div>
                </motion.button>
            </div>
            
            {/* Shooting Einverständnis Link Card - Same spacing as cards above */}
            {canViewEinverstaendnis && (
                <Card className="mt-4 bg-gradient-to-br from-primary/10 to-primary/10 border border-primary/20">
                    <CardContent className="p-6">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                                    <PenTool className="w-7 h-7 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-lg">Shooting Einverständnis Link</h3>
                                    <p className="text-sm text-muted-foreground">QR-Code für spontane Einverständniserklärungen vor Ort</p>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        const fullUrl = `${window.location.origin}/einverstaendnis`;
                                        navigator.clipboard.writeText(fullUrl).then(() => {
                                            toast.success('Link kopiert!', {
                                                description: fullUrl
                                            });
                                        }).catch(() => {
                                            toast.error('Kopieren fehlgeschlagen');
                                        });
                                    }}
                                    className="gap-2 flex-1"
                                    data-testid="copy-einverstaendnis-link"
                                >
                                    <Copy className="w-4 h-4" />
                                    Link kopieren
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setShowQrModal(true)}
                                    className="gap-2 flex-1"
                                    data-testid="show-einverstaendnis-qr"
                                >
                                    <QrCode className="w-4 h-4" />
                                    QR-Code anzeigen
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
            
            {/* Client Intake Link Card */}
            {canViewEinverstaendnis && (
                <Card className="mt-4 bg-gradient-to-br from-primary/10 to-primary/10 border border-primary/20">
                    <CardContent className="p-6">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                                    <Building2 className="w-7 h-7 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-lg">Client Intake Link</h3>
                                    <p className="text-sm text-muted-foreground">Neukunden-Erfassungsformular</p>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        const fullUrl = `${window.location.origin}/client-intake`;
                                        navigator.clipboard.writeText(fullUrl).then(() => {
                                            toast.success('Link kopiert!', { description: fullUrl });
                                        }).catch(() => {
                                            toast.error('Kopieren fehlgeschlagen');
                                        });
                                    }}
                                    className="gap-2 flex-1"
                                    data-testid="copy-intake-link"
                                >
                                    <Copy className="w-4 h-4" />
                                    Link kopieren
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setShowIntakeQrModal(true)}
                                    className="gap-2 flex-1"
                                    data-testid="show-intake-qr"
                                >
                                    <QrCode className="w-4 h-4" />
                                    QR-Code anzeigen
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
            
            {/* Tabs for Lists */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="w-full flex flex-wrap gap-1 h-auto p-1">
                    <TabsTrigger value="overview" className="gap-2 flex-1 min-w-fit">
                        <FileText className="w-4 h-4 hidden sm:block" />
                        <span className="text-xs sm:text-sm">Meine</span>
                    </TabsTrigger>
                    {canViewAll && (
                        <TabsTrigger value="admin" className="gap-2 flex-1 min-w-fit">
                            <Users className="w-4 h-4 hidden sm:block" />
                            <span className="text-xs sm:text-sm">Alle</span>
                        </TabsTrigger>
                    )}
                    {canViewEinverstaendnis && (
                        <TabsTrigger value="einverstaendnis" className="gap-2 flex-1 min-w-fit">
                            <PenTool className="w-4 h-4 hidden sm:block" />
                            <span className="text-xs sm:text-sm">Einverständnisse</span>
                            {einverstaendnisse.length > 0 && (
                                <Badge variant="secondary" className="ml-1 text-xs">
                                    {einverstaendnisse.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                    )}
                    {canViewEinverstaendnis && (
                        <TabsTrigger value="client-intake" className="gap-2 flex-1 min-w-fit">
                            <Building2 className="w-4 h-4 hidden sm:block" />
                            <span className="text-xs sm:text-sm">Client Intake</span>
                            {clientIntakes.length > 0 && (
                                <Badge variant="secondary" className="ml-1 text-xs">
                                    {clientIntakes.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="extra" className="gap-2 flex-1 min-w-fit" data-testid="tab-extra-services">
                        <Euro className="w-4 h-4 hidden sm:block" />
                        <span className="text-xs sm:text-sm">Extra-Leistungen</span>
                        {extraServices.filter(s => s.status === 'offen').length > 0 && (
                            <Badge variant="destructive" className="ml-1 text-xs">
                                {extraServices.filter(s => s.status === 'offen').length}
                            </Badge>
                        )}
                    </TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview" className="mt-4 space-y-6">
                    {/* My Shooting Docs */}
                    <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <Camera className="w-4 h-4 text-primary" />
                            Meine Shootings
                        </h3>
                        {shootingDocs.length === 0 ? (
                            <p className="text-muted-foreground text-sm">Keine Dokumentationen vorhanden</p>
                        ) : (
                            <div className="space-y-2">
                                {shootingDocs.slice(0, 5).map(doc => (
                                    <motion.div
                                        key={doc.id}
                                        whileHover={{ scale: 1.01 }}
                                        onClick={() => openViewModal(doc, 'shooting')}
                                        className="p-4 rounded-xl bg-card border cursor-pointer hover:border-primary/50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-semibold">{doc.customer_name || 'Kein Kunde'}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {format(parseISO(doc.shooting_date), 'dd. MMMM yyyy', { locale: de })} • {doc.start_time} - {doc.end_time}
                                                </p>
                                            </div>
                                            <Eye className="w-4 h-4 text-muted-foreground" />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* My Expenses */}
                    <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <Receipt className="w-4 h-4 text-primary" />
                            Meine Spesen
                        </h3>
                        {expenses.length === 0 ? (
                            <p className="text-muted-foreground text-sm">Keine Spesen vorhanden</p>
                        ) : (
                            <div className="space-y-2">
                                {expenses.slice(0, 5).map(exp => (
                                    <motion.div
                                        key={exp.id}
                                        whileHover={{ scale: 1.01 }}
                                        onClick={() => openViewModal(exp, 'expense')}
                                        className="p-4 rounded-xl bg-card border cursor-pointer hover:border-primary/50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <span className="text-xl">{EXPENSE_CATEGORIES.find(c => c.value === exp.category)?.icon || '📦'}</span>
                                                <div>
                                                    <p className="font-medium">{exp.category} • {exp.amount.toFixed(2)} €</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {format(parseISO(exp.expense_date), 'dd.MM.yyyy')}
                                                        {exp.customer_name && ` • ${exp.customer_name}`}
                                                    </p>
                                                </div>
                                            </div>
                                            <Badge variant={
                                                exp.status === 'approved' ? 'default' :
                                                exp.status === 'rejected' ? 'destructive' : 'secondary'
                                            }>
                                                {exp.status === 'approved' ? 'Genehmigt' :
                                                 exp.status === 'rejected' ? 'Abgelehnt' : 'Eingereicht'}
                                            </Badge>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>
                
                {canViewAll && (
                    <TabsContent value="admin" className="mt-4 space-y-6">
                        {/* Admin: All Expenses */}
                        <div>
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Receipt className="w-4 h-4 text-primary" />
                                Alle Spesen
                                {expenses.filter(e => e.status === 'submitted').length > 0 && (
                                    <Badge variant="destructive" className="ml-2">
                                        {expenses.filter(e => e.status === 'submitted').length} offen
                                    </Badge>
                                )}
                            </h3>
                            <div className="space-y-2">
                                {expenses.map(exp => (
                                    <motion.div
                                        key={exp.id}
                                        whileHover={{ scale: 1.01 }}
                                        onClick={() => openViewModal(exp, 'expense')}
                                        className="p-4 rounded-xl bg-card border cursor-pointer hover:border-primary/50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <span className="text-xl">{EXPENSE_CATEGORIES.find(c => c.value === exp.category)?.icon || '📦'}</span>
                                                <div>
                                                    <p className="font-medium">{exp.category} • {exp.amount.toFixed(2)} €</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {exp.created_by_name} • {format(parseISO(exp.expense_date), 'dd.MM.yyyy')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant={
                                                    exp.status === 'approved' ? 'default' :
                                                    exp.status === 'rejected' ? 'destructive' : 'secondary'
                                                }>
                                                    {exp.status === 'approved' ? 'Genehmigt' :
                                                     exp.status === 'rejected' ? 'Abgelehnt' : 'Offen'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Admin: All Shooting Docs */}
                        <div>
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Camera className="w-4 h-4 text-primary" />
                                Alle Shooting-Dokumentationen
                            </h3>
                            <div className="space-y-2">
                                {shootingDocs.map(doc => (
                                    <motion.div
                                        key={doc.id}
                                        whileHover={{ scale: 1.01 }}
                                        onClick={() => openViewModal(doc, 'shooting')}
                                        className="p-4 rounded-xl bg-card border cursor-pointer hover:border-primary/50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-semibold">{doc.customer_name || 'Kein Kunde'}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {format(parseISO(doc.shooting_date), 'dd. MMM yyyy', { locale: de })} • {doc.created_by_name} • {doc.start_time} - {doc.end_time}
                                                </p>
                                            </div>
                                            <Eye className="w-4 h-4 text-muted-foreground" />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </TabsContent>
                )}
                
                {/* Einverständnis Tab */}
                {canViewEinverstaendnis && (
                    <TabsContent value="einverstaendnis" className="mt-4 space-y-6">
                        {/* Filter by Firma */}
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1 max-w-sm">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Nach Firma filtern..."
                                    value={companyFilter}
                                    onChange={(e) => setCompanyFilter(e.target.value)}
                                    className="pl-9"
                                    data-testid="company-filter"
                                />
                            </div>
                            {companyFilter && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setCompanyFilter('')}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                        
                        {/* Einverständnis List */}
                        <div>
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <PenTool className="w-4 h-4 text-primary" />
                                Shooting Einverständnisse
                                <Badge variant="secondary">{einverstaendnisse.filter(e => 
                                    !companyFilter || e.company_name?.toLowerCase().includes(companyFilter.toLowerCase())
                                ).length}</Badge>
                            </h3>
                            
                            {einverstaendnisse.length === 0 ? (
                                <div className="text-center py-12">
                                    <PenTool className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                    <p className="text-muted-foreground">Noch keine Einverständniserklärungen eingereicht</p>
                                    <p className="text-sm text-muted-foreground mt-1">Teile den QR-Code, damit Teilnehmer vor Ort unterschreiben können</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {einverstaendnisse
                                        .filter(e => !companyFilter || e.company_name?.toLowerCase().includes(companyFilter.toLowerCase()))
                                        .map(entry => (
                                        <motion.div
                                            key={entry.id}
                                            whileHover={{ scale: 1.01 }}
                                            onClick={() => openViewModal({ ...entry, type: 'einverstaendnis' }, 'einverstaendnis')}
                                            className="p-4 rounded-xl bg-card border cursor-pointer hover:border-primary/50 transition-colors"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                                        <Building2 className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium flex items-center gap-2">
                                                            {entry.first_name} {entry.last_name}
                                                            <Badge className="bg-primary/20 text-primary border-primary/30">
                                                                {entry.company_name}
                                                            </Badge>
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {entry.shooting_date ? format(parseISO(entry.shooting_date), 'dd.MM.yyyy') : '-'}
                                                            {entry.email && ` • ${entry.email}`}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {entry.signature_url && (
                                                        <Badge variant="outline" className="text-green-600 border-green-600/30">
                                                            <Check className="w-3 h-3 mr-1" />
                                                            Unterschrieben
                                                        </Badge>
                                                    )}
                                                    <Eye className="w-4 h-4 text-muted-foreground" />
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabsContent>
                )}
                
                {/* Client Intake Tab */}
                {canViewEinverstaendnis && (
                    <TabsContent value="client-intake" className="mt-4 space-y-6">
                        {/* Filter by Company */}
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1 max-w-sm">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Nach Firma filtern..."
                                    value={intakeFilter}
                                    onChange={(e) => setIntakeFilter(e.target.value)}
                                    className="pl-9"
                                    data-testid="intake-filter"
                                />
                            </div>
                            {intakeFilter && (
                                <Button variant="ghost" size="icon" onClick={() => setIntakeFilter('')}>
                                    <X className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                        
                        {/* Client Intake List */}
                        <div>
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-primary" />
                                Client Intake Einreichungen
                                <Badge variant="secondary">{clientIntakes.filter(e => 
                                    !intakeFilter || e.company_name?.toLowerCase().includes(intakeFilter.toLowerCase())
                                ).length}</Badge>
                            </h3>
                            
                            {clientIntakes.length === 0 ? (
                                <div className="text-center py-12">
                                    <Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                    <p className="text-muted-foreground">Noch keine Client Intake Formulare eingereicht</p>
                                    <p className="text-sm text-muted-foreground mt-1">Teile den Link mit Neukunden</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {clientIntakes
                                        .filter(e => !intakeFilter || e.company_name?.toLowerCase().includes(intakeFilter.toLowerCase()))
                                        .map(entry => (
                                        <motion.div
                                            key={entry.id}
                                            whileHover={{ scale: 1.01 }}
                                            onClick={() => openViewModal({ ...entry, type: 'client-intake' }, 'client-intake')}
                                            className="p-4 rounded-xl bg-card border cursor-pointer hover:border-primary/50 transition-colors"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                                        <Building2 className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium flex items-center gap-2">
                                                            <Badge className="bg-primary/20 text-primary border-primary/30">
                                                                {entry.company_name}
                                                            </Badge>
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {entry.first_name} {entry.last_name}
                                                            {entry.created_at && ` • ${format(parseISO(entry.created_at), 'dd.MM.yyyy')}`}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Eye className="w-4 h-4 text-muted-foreground" />
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabsContent>
                )}

                {/* Extra-Leistungen Tab */}
                <TabsContent value="extra" className="mt-4 space-y-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <h2 className="text-lg font-semibold">Extra-Leistungen</h2>
                        <div className="flex items-center gap-2">
                            <Select value={extraFilter} onValueChange={setExtraFilter}>
                                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="extra-filter">
                                    <SelectValue placeholder="Alle" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">Alle</SelectItem>
                                    <SelectItem value="offen">Offen</SelectItem>
                                    <SelectItem value="verrechnet">Verrechnet</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button size="sm" onClick={() => setShowExtraModal(true)} data-testid="add-extra-service-btn">
                                <Plus className="w-4 h-4 mr-1" /> Neue Leistung
                            </Button>
                        </div>
                    </div>

                    {extraServices.filter(s => extraFilter === '__all__' || s.status === extraFilter).length === 0 ? (
                        <div className="text-center py-12">
                            <Euro className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                            <p className="text-muted-foreground">Keine Extra-Leistungen</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {extraServices
                                .filter(s => extraFilter === '__all__' || s.status === extraFilter)
                                .map(service => (
                                <Card key={service.id} data-testid={`extra-service-${service.id}`} className={service.status === 'verrechnet' ? 'opacity-60' : ''}>
                                    <CardContent className="p-3 sm:p-4">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-semibold text-sm">{service.service_title}</h3>
                                                    <Badge variant={service.status === 'offen' ? 'destructive' : 'secondary'} className="text-[10px]">
                                                        {service.status === 'offen' ? 'Offen' : 'Verrechnet'}
                                                    </Badge>
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-0.5">
                                                    {service.customer_name || 'Kein Kunde'} &middot; {service.month} &middot; von {service.created_by_name}
                                                </p>
                                                {service.description && (
                                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{service.description}</p>
                                                )}
                                                {service.notes && (
                                                    <details className="text-xs mt-1">
                                                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">📝 Notizen anzeigen</summary>
                                                        <p className="mt-1 p-2 rounded-lg bg-muted/40 whitespace-pre-wrap">{service.notes}</p>
                                                    </details>
                                                )}
                                                {service.attachments && service.attachments.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                        {service.attachments.map(a => (
                                                            <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted/60 hover:bg-muted text-[10px] truncate max-w-[200px]" data-testid={`extra-attachment-link-${a.id}`}>
                                                                📎 {a.name}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                                {service.service_date && (
                                                    <p className="text-[10px] text-muted-foreground mt-1">
                                                        Datum: {format(parseISO(service.service_date), 'd. MMM yyyy', { locale: de })}
                                                    </p>
                                                )}
                                                {service.invoiced_at && (
                                                    <p className="text-[10px] text-green-600 mt-1">
                                                        Verrechnet am {format(parseISO(service.invoiced_at), 'd. MMM yyyy', { locale: de })} von {service.invoiced_by_name}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                {isAdmin && service.status === 'offen' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-xs gap-1 text-green-600 border-green-200 hover:bg-green-50"
                                                        data-testid={`invoice-extra-${service.id}`}
                                                        onClick={async () => {
                                                            try {
                                                                await api.put(`/extra-services/${service.id}`, { status: 'verrechnet' });
                                                                toast.success('Als verrechnet markiert');
                                                                fetchData();
                                                            } catch (e) { toast.error('Fehler'); }
                                                        }}
                                                    >
                                                        <CheckCircle className="w-3.5 h-3.5" /> Verrechnet
                                                    </Button>
                                                )}
                                                {isAdmin && service.status === 'verrechnet' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 text-xs"
                                                        onClick={async () => {
                                                            try {
                                                                await api.put(`/extra-services/${service.id}`, { status: 'offen' });
                                                                toast.success('Wieder auf offen gesetzt');
                                                                fetchData();
                                                            } catch (e) { toast.error('Fehler'); }
                                                        }}
                                                    >
                                                        Zuruecksetzen
                                                    </Button>
                                                )}
                                                {(isAdmin || service.created_by === user?.id) && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-destructive"
                                                        onClick={async () => {
                                                            if (!window.confirm('Leistung loeschen?')) return;
                                                            try {
                                                                await api.delete(`/extra-services/${service.id}`);
                                                                toast.success('Geloescht');
                                                                fetchData();
                                                            } catch (e) { toast.error('Fehler'); }
                                                        }}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

            </Tabs>
            
            {/* Wizard Modal */}
            <AnimatePresence>
                {showWizard && (
                    <FormWizard
                        type={wizardType}
                        onClose={closeWizard}
                        onComplete={handleWizardComplete}
                        api={api}
                        customers={customers}
                        prefill={wizardPrefill}
                    />
                )}
            </AnimatePresence>
            
            {/* View Modal */}
            <ViewModal
                item={viewItem}
                open={showViewModal}
                onClose={() => setShowViewModal(false)}
                canApprove={canApprove}
                onStatusUpdate={handleStatusUpdate}
                api={api}
                onUpdated={(updated) => {
                    setViewItem({ ...updated, type: 'einverstaendnis' });
                    setEinverstaendnisse(prev => prev.map(e => e.id === updated.id ? updated : e));
                }}
            />
            
            {/* QR Code Modal for Einverständnis */}
            <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
                <DialogContent className="max-w-xs mx-4 max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <QrCode className="w-4 h-4 text-primary" />
                            QR-Code
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-3 py-2">
                        <div className="bg-white p-3 rounded-xl shadow-lg">
                            <QRCodeSVG 
                                value={`${window.location.origin}/einverstaendnis`}
                                size={160}
                                level="H"
                                includeMargin={true}
                            />
                        </div>
                        <p className="text-xs text-muted-foreground text-center">
                            Für Shooting Einverständnis scannen
                        </p>
                        <div className="flex gap-2 w-full">
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => {
                                    // Download QR code as PNG
                                    const svg = document.querySelector('.qr-einverstaendnis-target');
                                    if (svg) {
                                        const canvas = document.createElement('canvas');
                                        const ctx = canvas.getContext('2d');
                                        const img = new Image();
                                        const svgData = new XMLSerializer().serializeToString(svg);
                                        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                                        const url = URL.createObjectURL(svgBlob);
                                        
                                        img.onload = () => {
                                            canvas.width = 300;
                                            canvas.height = 300;
                                            ctx.fillStyle = 'white';
                                            ctx.fillRect(0, 0, 300, 300);
                                            ctx.drawImage(img, 50, 50, 200, 200);
                                            
                                            const pngUrl = canvas.toDataURL('image/png');
                                            const downloadLink = document.createElement('a');
                                            downloadLink.href = pngUrl;
                                            downloadLink.download = 'shooting-einverstaendnis-qrcode.png';
                                            downloadLink.click();
                                            URL.revokeObjectURL(url);
                                            toast.success('QR-Code heruntergeladen!');
                                        };
                                        img.src = url;
                                    }
                                }}
                                data-testid="download-einverstaendnis-qr"
                            >
                                <Download className="w-3 h-3 mr-1" />
                                PNG
                            </Button>
                            <Button
                                size="sm"
                                className="flex-1"
                                onClick={() => setShowQrModal(false)}
                            >
                                Schließen
                            </Button>
                        </div>
                    </div>
                    {/* Hidden QR for download */}
                    <div className="hidden">
                        <QRCodeSVG 
                            value={`${window.location.origin}/einverstaendnis`}
                            size={200}
                            level="H"
                            includeMargin={true}
                            className="qr-einverstaendnis-target"
                        />
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* QR Code Modal for Client Intake */}
            <Dialog open={showIntakeQrModal} onOpenChange={setShowIntakeQrModal}>
                <DialogContent className="max-w-xs mx-4 max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <QrCode className="w-4 h-4 text-primary" />
                            Client Intake QR-Code
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-3 py-2">
                        <div className="bg-white p-3 rounded-xl shadow-lg">
                            <QRCodeSVG 
                                value={`${window.location.origin}/client-intake`}
                                size={160}
                                level="H"
                                includeMargin={true}
                            />
                        </div>
                        <p className="text-xs text-muted-foreground text-center">
                            Für Client Intake Formular scannen
                        </p>
                        <div className="flex gap-2 w-full">
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => {
                                    const svg = document.querySelector('.qr-intake-target');
                                    if (svg) {
                                        const canvas = document.createElement('canvas');
                                        const ctx = canvas.getContext('2d');
                                        const img = new Image();
                                        const svgData = new XMLSerializer().serializeToString(svg);
                                        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                                        const url = URL.createObjectURL(svgBlob);
                                        
                                        img.onload = () => {
                                            canvas.width = 300;
                                            canvas.height = 300;
                                            ctx.fillStyle = 'white';
                                            ctx.fillRect(0, 0, 300, 300);
                                            ctx.drawImage(img, 50, 50, 200, 200);
                                            
                                            const pngUrl = canvas.toDataURL('image/png');
                                            const downloadLink = document.createElement('a');
                                            downloadLink.href = pngUrl;
                                            downloadLink.download = 'client-intake-qrcode.png';
                                            downloadLink.click();
                                            URL.revokeObjectURL(url);
                                            toast.success('QR-Code heruntergeladen!');
                                        };
                                        img.src = url;
                                    }
                                }}
                                data-testid="download-intake-qr"
                            >
                                <Download className="w-3 h-3 mr-1" />
                                PNG
                            </Button>
                            <Button
                                size="sm"
                                className="flex-1"
                                onClick={() => setShowIntakeQrModal(false)}
                            >
                                Schließen
                            </Button>
                        </div>
                    </div>
                    {/* Hidden QR for download */}
                    <div className="hidden">
                        <QRCodeSVG 
                            value={`${window.location.origin}/client-intake`}
                            size={160}
                            level="H"
                            includeMargin={true}
                            className="qr-intake-target"
                        />
                    </div>
                </DialogContent>
            </Dialog>

            {/* Extra-Leistung Modal */}
            <Dialog open={showExtraModal} onOpenChange={setShowExtraModal}>
                <DialogContent className="max-w-md p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="text-base flex items-center gap-2">
                            <Euro className="w-4 h-4 text-amber-600" />
                            Extra-Leistung erfassen
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Kunde *</Label>
                            <Select
                                value={extraForm.customer_id || '__manual__'}
                                onValueChange={(v) => {
                                    if (v === '__manual__') {
                                        setExtraForm(f => ({ ...f, customer_id: '', customer_name_manual: '' }));
                                    } else {
                                        const c = customers.find(c => c.id === v);
                                        setExtraForm(f => ({ ...f, customer_id: v, customer_name_manual: c?.name || '' }));
                                    }
                                }}
                            >
                                <SelectTrigger className="h-9 text-sm" data-testid="extra-customer-select">
                                    <SelectValue placeholder="Kunde waehlen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__manual__">Manuell eingeben</SelectItem>
                                    {customers.filter(c => !c.is_archived).map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {!extraForm.customer_id && (
                                <Input
                                    className="mt-1.5 h-9"
                                    placeholder="Kundenname eingeben..."
                                    value={extraForm.customer_name_manual}
                                    onChange={(e) => setExtraForm(f => ({ ...f, customer_name_manual: e.target.value }))}
                                    data-testid="extra-customer-manual"
                                />
                            )}
                        </div>
                        <div>
                            <Label className="text-xs">Leistung *</Label>
                            <Input
                                value={extraForm.service_title}
                                onChange={(e) => setExtraForm(f => ({ ...f, service_title: e.target.value }))}
                                placeholder="z.B. 5 zusaetzliche Postings"
                                data-testid="extra-service-title"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Beschreibung (optional)</Label>
                            <Textarea
                                value={extraForm.description}
                                onChange={(e) => setExtraForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Kurzbeschreibung..."
                                rows={2}
                                className="text-sm"
                            />
                        </div>

                        <div>
                            <Label className="text-xs">Notizen (mehrzeilig, optional)</Label>
                            <Textarea
                                value={extraForm.notes}
                                onChange={(e) => setExtraForm(f => ({ ...f, notes: e.target.value }))}
                                placeholder="Detail-Notizen, Workflow-Schritte, etc."
                                rows={4}
                                className="text-sm"
                                data-testid="extra-notes"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs">Datum</Label>
                                <Input
                                    type="date"
                                    value={extraForm.service_date}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setExtraForm(f => ({ ...f, service_date: v, month: v ? v.slice(0, 7) : f.month }));
                                    }}
                                    className="h-9"
                                    data-testid="extra-service-date"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Monat</Label>
                                <Input
                                    type="month"
                                    value={extraForm.month}
                                    onChange={(e) => setExtraForm(f => ({ ...f, month: e.target.value }))}
                                    className="h-9"
                                    data-testid="extra-month"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs">Anhänge (Bilder, PDF, ...)</Label>
                            <div className="mt-1 rounded-xl border border-dashed border-border p-3">
                                <input
                                    id="extra-file-input"
                                    type="file"
                                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                                    multiple
                                    className="hidden"
                                    onChange={async (e) => {
                                        const files = Array.from(e.target.files || []);
                                        if (!files.length) return;
                                        setExtraUploading(true);
                                        try {
                                            const uploaded = [];
                                            for (const file of files) {
                                                const fd = new FormData();
                                                fd.append('file', file);
                                                const resp = await api.post('/extra-services/upload', fd, {
                                                    headers: { 'Content-Type': 'multipart/form-data' },
                                                });
                                                uploaded.push(resp.data);
                                            }
                                            setExtraForm(f => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] }));
                                            toast.success(`${uploaded.length} Datei(en) hochgeladen`);
                                        } catch (err) {
                                            toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
                                        } finally {
                                            setExtraUploading(false);
                                            e.target.value = '';
                                        }
                                    }}
                                />
                                <label htmlFor="extra-file-input" className="flex items-center justify-center gap-2 text-xs cursor-pointer text-muted-foreground hover:text-foreground">
                                    {extraUploading ? '⏳ Lade hoch...' : '📎 Dateien auswählen'}
                                </label>
                            </div>
                            {extraForm.attachments && extraForm.attachments.length > 0 && (
                                <ul className="mt-2 space-y-1">
                                    {extraForm.attachments.map((a) => (
                                        <li key={a.id} className="flex items-center justify-between text-xs bg-muted/50 rounded-lg px-2 py-1.5">
                                            <a href={a.url} target="_blank" rel="noopener noreferrer" className="truncate flex-1 hover:underline" data-testid={`extra-attachment-${a.id}`}>
                                                {a.name}
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => setExtraForm(f => ({ ...f, attachments: (f.attachments || []).filter(x => x.id !== a.id) }))}
                                                className="ml-2 text-destructive hover:text-destructive/80"
                                                aria-label="Entfernen"
                                            >
                                                ✕
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowExtraModal(false)}>Abbrechen</Button>
                            <Button
                                size="sm"
                                className="flex-1"
                                data-testid="submit-extra-service"
                                onClick={async () => {
                                    if (!extraForm.service_title.trim()) {
                                        toast.error('Leistung ist erforderlich');
                                        return;
                                    }
                                    if (!extraForm.customer_id && !extraForm.customer_name_manual.trim()) {
                                        toast.error('Kunde ist erforderlich');
                                        return;
                                    }
                                    try {
                                        await api.post('/extra-services', {
                                            customer_id: extraForm.customer_id || null,
                                            customer_name_manual: extraForm.customer_name_manual || null,
                                            service_title: extraForm.service_title,
                                            description: extraForm.description || null,
                                            notes: extraForm.notes || null,
                                            service_date: extraForm.service_date || null,
                                            month: extraForm.month,
                                            attachments: extraForm.attachments || []
                                        });
                                        toast.success('Extra-Leistung erfasst!');
                                        setShowExtraModal(false);
                                        setExtraForm({ customer_id: '', customer_name_manual: '', service_title: '', description: '', notes: '', service_date: new Date().toISOString().slice(0, 10), month: new Date().toISOString().slice(0, 7), attachments: [] });
                                        setActiveTab('extra');
                                        fetchData();
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || 'Fehler');
                                    }
                                }}
                            >
                                Absenden
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            </div>
        </div>
    );
};

// ============ FORM WIZARD COMPONENT ============

const FormWizard = ({ type, onClose, onComplete, api, customers, prefill = null }) => {
    const { user: currentUser } = useAuth();
    // Aus einem Kalendertermin vorausgefuellt: direkt beim Anwesenheits-Check starten
    const [step, setStep] = useState(prefill && type === 'shooting' ? 4 : 0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [teamUsers, setTeamUsers] = useState([]);

    useEffect(() => {
        if (type !== 'shooting') return;
        api.get('/users').then(res => {
            setTeamUsers((res.data || []).filter(u => u.is_active !== false && u.role !== 'influencer'));
        }).catch(() => setTeamUsers([]));
    }, [api, type]);
    
    // Shooting form data - Kunde, Team und Social Faces sind Verweise auf die Datenbank (kein Freitext)
    const [shootingData, setShootingData] = useState(() => ({
        customer_id: prefill?.customer_id || '',
        customer_name: prefill?.customer_name || '',
        shooting_date: prefill?.shooting_date || format(new Date(), 'yyyy-MM-dd'),
        start_time: prefill?.start_time || '09:00',
        end_time: prefill?.end_time || '17:00',
        team_members: (prefill?.team_members?.length ? prefill.team_members : [{
            user_id: currentUser?.id || '', first_name: currentUser?.first_name || '', last_name: currentUser?.last_name || '', position: 'Shootingleitung'
        }]),
        social_faces: prefill?.social_faces || [],
        notes: '',
        note_images: [],
        calendar_event_id: prefill?.calendar_event_id || null,
        sf_shooting_id: prefill?.sf_shooting_id || null
    }));
    
    // Expense form data
    const [expenseData, setExpenseData] = useState({
        expense_date: format(new Date(), 'yyyy-MM-dd'),
        category: '',
        amount: '',
        currency: 'EUR',
        receipt_url: '',
        customer_id: '',
        customer_name: '',
        description: ''
    });
    
    const shootingSteps = [
        { title: 'Kunde', icon: Building2 },
        { title: 'Datum', icon: Calendar },
        { title: 'Uhrzeit', icon: Clock },
        { title: 'Team', icon: Users },
        { title: 'Social Faces', icon: Users },
        { title: 'Notizen', icon: FileText },
        { title: 'Zusammenfassung', icon: Check }
    ];
    
    const expenseSteps = [
        { title: 'Datum', icon: Calendar },
        { title: 'Kategorie', icon: FileText },
        { title: 'Betrag', icon: Euro },
        { title: 'Beleg', icon: Upload },
        { title: 'Kunde', icon: Users },
        { title: 'Beschreibung', icon: FileText },
        { title: 'Zusammenfassung', icon: Check }
    ];
    
    const steps = type === 'shooting' ? shootingSteps : expenseSteps;
    const totalSteps = steps.length;
    
    const canProceed = () => {
        if (type === 'shooting') {
            switch (step) {
                case 0: return !!shootingData.customer_id;  // Auswahl aus der Kundenliste ist Pflicht
                case 1: return !!shootingData.shooting_date;
                case 2: return !!shootingData.start_time && !!shootingData.end_time;
                case 3: return shootingData.team_members.some(m => m.user_id || (m.first_name && m.last_name));
                case 4: return shootingData.social_faces.every(f => f.check_in_time && f.check_out_time); // jede Person braucht eigene Zeiten
                case 5: return true; // Notes optional
                default: return true;
            }
        } else {
            switch (step) {
                case 0: return !!expenseData.expense_date;
                case 1: return !!expenseData.category;
                case 2: return !!expenseData.amount && parseFloat(expenseData.amount) > 0;
                case 3: return !!expenseData.receipt_url;
                case 4: return true; // Project optional
                case 5: return true; // Description optional
                default: return true;
            }
        }
    };
    
    const handleNext = () => {
        if (step < totalSteps - 1) {
            setStep(step + 1);
        }
    };
    
    const handleBack = () => {
        if (step > 0) {
            setStep(step - 1);
        }
    };
    
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            if (type === 'shooting') {
                // Leere Zeilen entfernen; Social Faces und Team gehen als IDs an den Server
                const cleanData = {
                    ...shootingData,
                    team_members: shootingData.team_members.filter(m => m.user_id || (m.first_name && m.last_name)),
                    social_faces: shootingData.social_faces.filter(f => f.social_face_id)
                };
                const res = await api.post('/forms/shooting', cleanData);
                const billing = res.data?.billing;
                if (billing) {
                    const hours = (billing.total_minutes / 60).toLocaleString('de-AT', { maximumFractionDigits: 2 });
                    toast.success(`Doku gespeichert, Shooting bestätigt: ${billing.participants.length} Social Faces, ${hours} h, € ${billing.total_amount.toFixed(2)}`);
                } else {
                    toast.success('Shooting-Dokumentation eingereicht!');
                }
            } else {
                const cleanData = {
                    ...expenseData,
                    amount: parseFloat(expenseData.amount)
                };
                await api.post('/forms/expenses', cleanData);
                toast.success('Spesenabrechnung eingereicht!');
            }
            onComplete();
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (detail && typeof detail === 'object') {
                toast.error([detail.message, ...(detail.errors || [])].filter(Boolean).join(' · '), { duration: 9000 });
            } else {
                toast.error(detail || 'Fehler beim Einreichen');
            }
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const [uploadingFile, setUploadingFile] = useState(false);
    
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Datei zu groß (max. 10MB)');
            return;
        }
        
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const res = await api.post('/forms/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data?.url) {
                setExpenseData(prev => ({ ...prev, receipt_url: res.data.url }));
                toast.success('Beleg hochgeladen');
            } else {
                throw new Error('Keine URL erhalten');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Fehler beim Hochladen');
        } finally {
            setUploadingFile(false);
        }
    };
    
    const addTeamMember = () => {
        setShootingData(prev => ({
            ...prev,
            team_members: [...prev.team_members, { user_id: '', first_name: '', last_name: '', position: '' }]
        }));
    };
    
    const removeTeamMember = (index) => {
        setShootingData(prev => ({
            ...prev,
            team_members: prev.team_members.filter((_, i) => i !== index)
        }));
    };
    
    // Social Face aus der Datenbank uebernehmen; Zeiten starten mit den Shooting-Zeiten und sind pro Person aenderbar
    const addSocialFace = (face) => {
        if (!face?.id) return;
        setShootingData(prev => prev.social_faces.some(f => f.social_face_id === face.id) ? prev : ({
            ...prev,
            social_faces: [...prev.social_faces, {
                social_face_id: face.id,
                first_name: face.first_name || (face.name || '').split(' ')[0] || '',
                last_name: face.last_name || (face.name || '').split(' ').slice(1).join(' '),
                check_in_time: prev.start_time || '',
                check_out_time: prev.end_time || ''
            }]
        }));
    };
    
    const removeSocialFace = (index) => {
        setShootingData(prev => ({
            ...prev,
            social_faces: prev.social_faces.filter((_, i) => i !== index)
        }));
    };
    
    const updateTeamMember = (index, field, value) => {
        setShootingData(prev => ({
            ...prev,
            team_members: prev.team_members.map((m, i) => i === index ? { ...m, [field]: value } : m)
        }));
    };
    
    const updateSocialFace = (index, field, value) => {
        setShootingData(prev => ({
            ...prev,
            social_faces: prev.social_faces.map((f, i) => i === index ? { ...f, [field]: value } : f)
        }));
    };
    
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-background"
        >
            <div className="h-full flex flex-col safe-area-inset">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b shrink-0">
                    <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                        <p className="text-sm text-muted-foreground">{step + 1} von {totalSteps}</p>
                        <p className="font-medium">{steps[step].title}</p>
                    </div>
                    <div className="w-9" />
                </div>
                
                {/* Progress Bar */}
                <div className="h-1 bg-muted shrink-0">
                    <motion.div
                        className="h-full bg-primary"
                        initial={{ width: 0 }}
                        animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
                    />
                </div>
                
                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 pb-8">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={step}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="max-w-lg mx-auto"
                        >
                            {type === 'shooting' ? (
                                <ShootingStep
                                    step={step}
                                    data={shootingData}
                                    setData={setShootingData}
                                    addTeamMember={addTeamMember}
                                    removeTeamMember={removeTeamMember}
                                    updateTeamMember={updateTeamMember}
                                    addSocialFace={addSocialFace}
                                    removeSocialFace={removeSocialFace}
                                    updateSocialFace={updateSocialFace}
                                    customers={customers}
                                    teamUsers={teamUsers}
                                    fromCalendar={!!prefill}
                                />
                            ) : (
                                <ExpenseStep
                                    step={step}
                                    data={expenseData}
                                    setData={setExpenseData}
                                    customers={customers}
                                    onFileUpload={handleFileUpload}
                                    isUploading={uploadingFile}
                                />
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
                
                {/* Footer Navigation */}
                <div className="p-4 pb-6 sm:pb-4 border-t flex items-center justify-between gap-4 shrink-0 bg-background">
                    <Button
                        variant="ghost"
                        onClick={handleBack}
                        disabled={step === 0}
                        className="gap-2"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Zurück
                    </Button>
                    
                    {step === totalSteps - 1 ? (
                        <Button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="gap-2 bg-primary hover:bg-primary/90"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Wird eingereicht...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" />
                                    Absenden
                                </>
                            )}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleNext}
                            disabled={!canProceed()}
                            className="gap-2"
                        >
                            Weiter
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

// ============ SHOOTING STEPS ============

const ShootingStep = ({ step, data, setData, addTeamMember, removeTeamMember, updateTeamMember, addSocialFace, removeSocialFace, updateSocialFace, customers, teamUsers = [], fromCalendar = false }) => {
    switch (step) {
        case 0:
            return <ShootingCustomerStep data={data} setData={setData} customers={customers} />;

        case 1:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Calendar className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Datum des Shootings</h2>
                        <p className="text-muted-foreground">Trage das Datum des Shootings ein.</p>
                    </div>
                    <Input
                        type="date"
                        value={data.shooting_date}
                        onChange={(e) => setData(prev => ({ ...prev, shooting_date: e.target.value }))}
                        className="h-14 text-lg text-center rounded-xl"
                    />
                </div>
            );
        
        case 2:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Clock className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Uhrzeit des Shootings</h2>
                        <p className="text-muted-foreground">Trage die Uhrzeit des Shootings ein.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-sm text-muted-foreground">Von</Label>
                            <Input
                                type="time"
                                value={data.start_time}
                                onChange={(e) => setData(prev => ({ ...prev, start_time: e.target.value }))}
                                className="h-14 text-lg text-center rounded-xl mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-sm text-muted-foreground">Bis</Label>
                            <Input
                                type="time"
                                value={data.end_time}
                                onChange={(e) => setData(prev => ({ ...prev, end_time: e.target.value }))}
                                className="h-14 text-lg text-center rounded-xl mt-1"
                            />
                        </div>
                    </div>
                </div>
            );
        
        case 3:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Users className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Team vor Ort</h2>
                        <p className="text-muted-foreground">Wer vom Team war dabei?</p>
                    </div>
                    <div className="space-y-4">
                        {data.team_members.map((member, index) => {
                            const isExternal = !member.user_id && (member._external || member.first_name || member.last_name);
                            return (
                                <div key={index} className="p-4 rounded-xl bg-muted/50 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium">Person {index + 1}</span>
                                        {data.team_members.length > 1 && (
                                            <button onClick={() => removeTeamMember(index)} className="p-1 hover:bg-destructive/20 rounded">
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </button>
                                        )}
                                    </div>
                                    <Select
                                        value={member.user_id || (isExternal ? '__extern__' : '')}
                                        onValueChange={(v) => {
                                            if (v === '__extern__') {
                                                setData(prev => ({ ...prev, team_members: prev.team_members.map((m, i) => i === index ? { ...m, user_id: '', first_name: '', last_name: '', _external: true } : m) }));
                                            } else {
                                                const u = teamUsers.find(x => x.id === v);
                                                setData(prev => ({ ...prev, team_members: prev.team_members.map((m, i) => i === index ? { ...m, user_id: v, first_name: u?.first_name || '', last_name: u?.last_name || '', _external: false } : m) }));
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="rounded-lg h-11" data-testid={`team-member-select-${index}`}>
                                            <SelectValue placeholder="Teammitglied wählen..." />
                                        </SelectTrigger>
                                        <SelectContent className="z-[200]">
                                            {teamUsers.map(u => (
                                                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                                            ))}
                                            <SelectItem value="__extern__">Extern (nicht im System)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {isExternal && (
                                        <div className="grid grid-cols-2 gap-3">
                                            <Input placeholder="Vorname" value={member.first_name} onChange={(e) => updateTeamMember(index, 'first_name', e.target.value)} className="rounded-lg h-11" />
                                            <Input placeholder="Nachname" value={member.last_name} onChange={(e) => updateTeamMember(index, 'last_name', e.target.value)} className="rounded-lg h-11" />
                                        </div>
                                    )}
                                    <Input
                                        placeholder="Position (z.B. Kameramann)"
                                        value={member.position}
                                        onChange={(e) => updateTeamMember(index, 'position', e.target.value)}
                                        className="rounded-lg h-11"
                                    />
                                </div>
                            );
                        })}
                        <Button variant="outline" onClick={addTeamMember} className="w-full gap-2">
                            <Plus className="w-4 h-4" />
                            Person hinzufügen
                        </Button>
                    </div>
                </div>
            );
        
        case 4:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Users className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Social Faces</h2>
                        <p className="text-muted-foreground">
                            Anwesenheitscheck: Check-in und Check-out pro Person.
                            {fromCalendar ? ' Die geplanten Personen sind schon eingetragen.' : ''}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Aus diesen Zeiten entsteht die Abrechnung je Person.</p>
                    </div>
                    <div className="space-y-4">
                        {data.social_faces.map((face, index) => (
                            <div key={face.social_face_id || index} className="p-4 rounded-xl bg-muted/50 space-y-3" data-testid={`doc-face-${index}`}>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-semibold">{face.first_name} {face.last_name}</span>
                                    <button onClick={() => removeSocialFace(index)} className="p-1 hover:bg-destructive/20 rounded" title="War nicht da / entfernen">
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-1 block">Check-In</Label>
                                        <Input
                                            type="time"
                                            value={face.check_in_time}
                                            onChange={(e) => updateSocialFace(index, 'check_in_time', e.target.value)}
                                            className="rounded-lg h-11"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-1 block">Check-Out</Label>
                                        <Input
                                            type="time"
                                            value={face.check_out_time}
                                            onChange={(e) => updateSocialFace(index, 'check_out_time', e.target.value)}
                                            className="rounded-lg h-11"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                        {data.social_faces.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center">Noch niemand eingetragen. Ohne Social Faces entsteht keine Abrechnung.</p>
                        )}
                        <div className="p-4 rounded-xl border border-dashed border-border space-y-2">
                            <Label className="text-xs text-muted-foreground">Person hinzufügen</Label>
                            <SocialFacePicker
                                allowCreate
                                excludeIds={data.social_faces.map(f => f.social_face_id)}
                                onSelect={addSocialFace}
                                inputClassName="h-11 rounded-lg"
                            />
                        </div>
                    </div>
                </div>
            );
        
        case 5:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <FileText className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Notizen / Anmerkungen</h2>
                        <p className="text-muted-foreground">Ist jemand zu spät gekommen? Equipment benötigt?</p>
                    </div>
                    <Textarea
                        value={data.notes}
                        onChange={(e) => setData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Notizen hier eingeben..."
                        className="min-h-[200px] rounded-xl"
                    />
                </div>
            );
        
        case 6:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Check className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Zusammenfassung</h2>
                        <p className="text-muted-foreground">Pruefe die Angaben vor dem Absenden.</p>
                    </div>
                    <div className="space-y-4 p-4 rounded-xl bg-muted/50">
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Kunde</span>
                            <span className="font-semibold text-primary">{data.customer_name || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Datum</span>
                            <span className="font-medium">{format(parseISO(data.shooting_date), 'dd.MM.yyyy')}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Uhrzeit</span>
                            <span className="font-medium">{data.start_time} - {data.end_time}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Team</span>
                            <span className="font-medium">{data.team_members.filter(m => m.user_id || m.first_name).length} Personen</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Social Faces</span>
                            <span className="font-medium">{data.social_faces.length} Personen</span>
                        </div>
                        {data.social_faces.map(f => (
                            <div key={f.social_face_id} className="flex justify-between py-1 text-sm">
                                <span>{f.first_name} {f.last_name}</span>
                                <span className="text-muted-foreground">{f.check_in_time} – {f.check_out_time}</span>
                            </div>
                        ))}
                        {data.notes && (
                            <div className="py-2">
                                <span className="text-muted-foreground block mb-1">Notizen</span>
                                <p className="text-sm">{data.notes.substring(0, 100)}...</p>
                            </div>
                        )}
                    </div>
                </div>
            );
        
        default:
            return null;
    }
};

// Kundenschritt der Shooting-Doku: Auswahl aus der Kundenliste (Pflicht, kein Freitext mehr)
const ShootingCustomerStep = ({ data, setData, customers }) => {
    const [search, setSearch] = useState('');
    const active = (customers || []).filter(c => !c.is_archived);
    const filtered = active.filter(c => (c.name || '').toLowerCase().includes(search.trim().toLowerCase()));
    return (
        <div className="space-y-5">
            <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-xl font-bold">Kunde</h2>
                <p className="text-muted-foreground">Fuer welchen Kunden war das Shooting?</p>
            </div>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Kunde suchen..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-12 pl-10 rounded-xl"
                    data-testid="doc-customer-search"
                />
            </div>
            <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                {filtered.map(c => (
                    <button
                        key={c.id}
                        type="button"
                        onClick={() => setData(prev => ({ ...prev, customer_id: c.id, customer_name: c.name }))}
                        className={`w-full text-left p-3 rounded-xl transition-all ${
                            data.customer_id === c.id
                                ? 'bg-primary/10 border-2 border-primary font-semibold'
                                : 'bg-muted/50 border-2 border-transparent hover:bg-muted'
                        }`}
                    >
                        {c.name}
                    </button>
                ))}
                {filtered.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                        Kein Kunde gefunden. Der Kunde muss zuerst in der Kundenliste angelegt sein.
                    </p>
                )}
            </div>
        </div>
    );
};

// ============ EXPENSE STEPS ============

const ExpenseStep = ({ step, data, setData, customers, onFileUpload, isUploading }) => {
    switch (step) {
        case 0:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Calendar className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Datum der Ausgabe</h2>
                        <p className="text-muted-foreground">Wann wurde die Ausgabe getätigt?</p>
                    </div>
                    <Input
                        type="date"
                        value={data.expense_date}
                        onChange={(e) => setData(prev => ({ ...prev, expense_date: e.target.value }))}
                        className="h-14 text-lg text-center rounded-xl"
                    />
                </div>
            );
        
        case 1:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <FileText className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Kategorie</h2>
                        <p className="text-muted-foreground">Wähle die Art der Ausgabe.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {EXPENSE_CATEGORIES.map(cat => (
                            <button
                                key={cat.value}
                                onClick={() => setData(prev => ({ ...prev, category: cat.value }))}
                                className={`p-4 rounded-xl border-2 transition-all text-left ${
                                    data.category === cat.value
                                        ? 'border-primary bg-primary/10'
                                        : 'border-muted hover:border-primary/50'
                                }`}
                            >
                                <span className="text-2xl block mb-1">{cat.icon}</span>
                                <span className="font-medium">{cat.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            );
        
        case 2:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Euro className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Betrag</h2>
                        <p className="text-muted-foreground">Wie viel wurde ausgegeben?</p>
                    </div>
                    <div className="relative">
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={data.amount}
                            onChange={(e) => setData(prev => ({ ...prev, amount: e.target.value }))}
                            placeholder="0.00"
                            className="h-16 text-2xl text-center rounded-xl pr-12"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-muted-foreground">€</span>
                    </div>
                </div>
            );
        
        case 3:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Upload className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Beleg hochladen</h2>
                        <p className="text-muted-foreground">Foto oder PDF der Rechnung.</p>
                    </div>
                    {isUploading ? (
                        <div className="p-8 rounded-xl border-2 border-dashed border-primary/50 text-center">
                            <div className="w-10 h-10 mx-auto mb-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <p className="font-medium">Wird hochgeladen...</p>
                        </div>
                    ) : data.receipt_url ? (
                        <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <FileImage className="w-8 h-8 text-primary" />
                                    <div>
                                        <p className="font-medium">Beleg hochgeladen</p>
                                        <p className="text-sm text-muted-foreground">Klicke um zu ändern</p>
                                    </div>
                                </div>
                                <Check className="w-5 h-5 text-primary" />
                            </div>
                        </div>
                    ) : (
                        <label className="block p-8 rounded-xl border-2 border-dashed border-muted hover:border-primary/50 transition-colors cursor-pointer text-center">
                            <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                            <p className="font-medium">Datei auswählen</p>
                            <p className="text-sm text-muted-foreground">JPG, PNG oder PDF</p>
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                onChange={onFileUpload}
                                className="hidden"
                            />
                        </label>
                    )}
                </div>
            );
        
        case 4:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Users className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Kunde (optional)</h2>
                        <p className="text-muted-foreground">Für welchen Kunden war die Ausgabe?</p>
                    </div>
                    <Select
                        value={data.customer_id || '_none'}
                        onValueChange={(val) => {
                            const isNone = val === '_none';
                            const customer = !isNone ? customers.find(c => c.id === val) : null;
                            setData(prev => ({
                                ...prev,
                                customer_id: isNone ? '' : val,
                                customer_name: customer?.name || ''
                            }));
                        }}
                    >
                        <SelectTrigger className="h-14 rounded-xl">
                            <SelectValue placeholder="Auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="_none">Nicht zugeordnet</SelectItem>
                            {customers.map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            );
        
        case 5:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <FileText className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Beschreibung</h2>
                        <p className="text-muted-foreground">Wofür wurde die Ausgabe getätigt?</p>
                    </div>
                    <Textarea
                        value={data.description}
                        onChange={(e) => setData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="z.B. Mittagessen mit Kunde XY..."
                        className="min-h-[150px] rounded-xl"
                    />
                </div>
            );
        
        case 6:
            return (
                <div className="space-y-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Check className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold">Zusammenfassung</h2>
                        <p className="text-muted-foreground">Prüfe die Angaben vor dem Absenden.</p>
                    </div>
                    <div className="space-y-4 p-4 rounded-xl bg-muted/50">
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Datum</span>
                            <span className="font-medium">{format(parseISO(data.expense_date), 'dd.MM.yyyy')}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Kategorie</span>
                            <span className="font-medium">{data.category}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Betrag</span>
                            <span className="font-medium text-lg">{parseFloat(data.amount).toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Kunde</span>
                            <span className="font-medium">{data.customer_name || 'Nicht angegeben'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                            <span className="text-muted-foreground">Beleg</span>
                            <span className="font-medium text-primary">Hochgeladen ✓</span>
                        </div>
                        {data.description && (
                            <div className="py-2">
                                <span className="text-muted-foreground block mb-1">Beschreibung</span>
                                <p className="text-sm">{data.description}</p>
                            </div>
                        )}
                    </div>
                </div>
            );
        
        default:
            return null;
    }
};

// ============ VIEW MODAL ============

const ViewModal = ({ item, open, onClose, canApprove, onStatusUpdate, api, onUpdated }) => {
    const [comment, setComment] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    // Reset edit state whenever the viewed item changes
    React.useEffect(() => {
        setEditMode(false);
        setEditForm({});
    }, [item?.id]);
    
    if (!item) return null;

    const startEdit = () => {
        setEditForm({
            company_name: item.company_name || '',
            first_name: item.first_name || '',
            last_name: item.last_name || '',
            email: item.email || '',
            phone: item.phone || '',
            shooting_date: item.shooting_date || '',
        });
        setEditMode(true);
    };

    const handleSaveEdit = async () => {
        // Only send fields that actually changed
        const payload = {};
        Object.entries(editForm).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== item[k]) payload[k] = v;
        });
        if (Object.keys(payload).length === 0) {
            setEditMode(false);
            return;
        }
        setSaving(true);
        try {
            const res = await api.put(`/shooting-einverstaendnis/${item.id}`, payload);
            toast.success('Einverständnis aktualisiert');
            setEditMode(false);
            if (onUpdated) onUpdated(res.data);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Fehler beim Speichern';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handlePdfDownload = async () => {
        setDownloading(true);
        try {
            // Get token from localStorage for direct URL download (mobile-friendly)
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_BACKEND_URL;
            
            let endpoint;
            if (item.type === 'shooting') {
                endpoint = `/api/forms/shooting/${item.id}/pdf/download?token=${token}`;
            } else if (item.type === 'einverstaendnis') {
                endpoint = `/api/shooting-einverstaendnis/${item.id}/pdf/download?token=${token}`;
            } else if (item.type === 'client-intake') {
                endpoint = `/api/client-intake/${item.id}/pdf/download?token=${token}`;
            } else {
                endpoint = `/api/forms/expenses/${item.id}/pdf/download?token=${token}`;
            }
            
            // Direct window.open triggers native browser download
            window.open(`${baseUrl}${endpoint}`, '_blank');
            toast.success('PDF wird heruntergeladen');
        } catch (err) {
            toast.error('Fehler beim PDF-Export');
            console.error(err);
        } finally {
            setDownloading(false);
        }
    };
    
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-sm max-h-[80vh] overflow-y-auto mx-4" data-testid="view-modal">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle className="flex items-center gap-2 text-base">
                            {item.type === 'shooting' ? (
                                <>
                                    <Camera className="w-5 h-5 text-primary" />
                                    Shooting-Dokumentation
                                </>
                            ) : item.type === 'einverstaendnis' ? (
                                <>
                                    <PenTool className="w-5 h-5 text-primary" />
                                    Shooting Einverständnis
                                </>
                            ) : item.type === 'client-intake' ? (
                                <>
                                    <Building2 className="w-5 h-5 text-primary" />
                                    Client Intake
                                </>
                            ) : (
                                <>
                                    <Receipt className="w-5 h-5 text-primary" />
                                    Spesenabrechnung
                                </>
                            )}
                        </DialogTitle>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePdfDownload}
                            disabled={downloading}
                            className="gap-1.5 ml-2 shrink-0"
                            data-testid="pdf-download-btn"
                        >
                            <Download className="w-3.5 h-3.5" />
                            {downloading ? '...' : 'PDF'}
                        </Button>
                    </div>
                </DialogHeader>
                
                <div className="space-y-4">
                    {item.type === 'einverstaendnis' ? (
                        <>
                            {editMode ? (
                                <div className="space-y-3" data-testid="edit-einverstaendnis-form">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Firma</Label>
                                        <Input
                                            value={editForm.company_name || ''}
                                            onChange={(e) => setEditForm(f => ({ ...f, company_name: e.target.value }))}
                                            placeholder="Firmenname richtigstellen..."
                                            data-testid="edit-ev-company-name"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Vorname</Label>
                                            <Input
                                                value={editForm.first_name || ''}
                                                onChange={(e) => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                                                data-testid="edit-ev-first-name"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Nachname</Label>
                                            <Input
                                                value={editForm.last_name || ''}
                                                onChange={(e) => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                                                data-testid="edit-ev-last-name"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Shooting-Datum</Label>
                                        <Input
                                            type="date"
                                            value={editForm.shooting_date || ''}
                                            onChange={(e) => setEditForm(f => ({ ...f, shooting_date: e.target.value }))}
                                            data-testid="edit-ev-shooting-date"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">E-Mail</Label>
                                        <Input
                                            type="email"
                                            value={editForm.email || ''}
                                            onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                                            data-testid="edit-ev-email"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Telefon</Label>
                                        <Input
                                            value={editForm.phone || ''}
                                            onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                                            data-testid="edit-ev-phone"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground italic">
                                        Hinweis: Unterschrift und Einverständnis bleiben aus Nachweisgründen unverändert.
                                    </p>
                                    <div className="flex gap-2 pt-1">
                                        <Button variant="outline" className="flex-1" disabled={saving} onClick={() => { setEditMode(false); setEditForm({}); }} data-testid="edit-ev-cancel">
                                            Abbrechen
                                        </Button>
                                        <Button className="flex-1" disabled={saving} onClick={handleSaveEdit} data-testid="edit-ev-save">
                                            <CheckCircle className="w-4 h-4 mr-2" />
                                            {saving ? 'Speichert…' : 'Speichern'}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <>
                            {/* Einverständnis Details */}
                            <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
                                <div className="flex items-center gap-3">
                                    <Building2 className="w-5 h-5 text-primary" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Firma</p>
                                        <p className="font-bold text-primary">{item.company_name}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Name</p>
                                    <p className="font-medium text-sm">{item.first_name} {item.last_name}</p>
                                </div>
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Shooting Datum</p>
                                    <p className="font-medium text-sm">{item.shooting_date ? format(parseISO(item.shooting_date), 'dd.MM.yyyy') : '-'}</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">E-Mail</p>
                                    <p className="font-medium text-xs truncate">{item.email}</p>
                                </div>
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Telefon</p>
                                    <p className="font-medium text-sm">{item.phone}</p>
                                </div>
                            </div>
                            
                            {item.signature_url && (
                                <div>
                                    <p className="text-sm font-medium mb-1">Unterschrift</p>
                                    <div className="p-2 rounded-lg bg-white border">
                                        <img 
                                            src={item.signature_url} 
                                            alt="Unterschrift" 
                                            className="max-h-20 mx-auto"
                                        />
                                    </div>
                                </div>
                            )}
                            
                            <div className="p-2 rounded-lg bg-muted/50">
                                <p className="text-xs text-muted-foreground">Eingereicht am</p>
                                <p className="font-medium text-sm">
                                    {item.created_at ? format(parseISO(item.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : '-'}
                                </p>
                            </div>
                            {item.updated_by_name && (
                                <p className="text-[10px] text-muted-foreground italic" data-testid="ev-audit-line">
                                    Zuletzt bearbeitet von {item.updated_by_name}
                                    {item.updated_at && ` am ${item.updated_at.slice(0, 10)}`}
                                </p>
                            )}
                            {canApprove && (
                                <Button variant="outline" size="sm" className="w-full" onClick={startEdit} data-testid="edit-ev-start">
                                    <Edit3 className="w-4 h-4 mr-2" /> Bearbeiten
                                </Button>
                            )}
                                </>
                            )}
                        </>
                    ) : item.type === 'client-intake' ? (
                        <>
                            {/* Client Intake Details */}
                            <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
                                <div className="flex items-center gap-3">
                                    <Building2 className="w-5 h-5 text-primary" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Firma</p>
                                        <p className="font-bold text-primary">{item.company_name}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Ansprechpartner</p>
                                    <p className="font-medium text-sm">{item.first_name} {item.last_name}</p>
                                </div>
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Position</p>
                                    <p className="font-medium text-sm">{item.position || '-'}</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">E-Mail</p>
                                    <p className="font-medium text-xs truncate">{item.email}</p>
                                </div>
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Telefon</p>
                                    <p className="font-medium text-sm">{item.phone || '-'}</p>
                                </div>
                            </div>
                            
                            {item.website && (
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Website</p>
                                    <a href={item.website.startsWith('http') ? item.website : `https://${item.website}`} target="_blank" rel="noopener noreferrer" className="font-medium text-sm text-primary truncate block">{item.website}</a>
                                </div>
                            )}
                            
                            {item.business_type && (
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Geschäftsart</p>
                                    <p className="font-medium text-sm">{item.business_type}</p>
                                </div>
                            )}
                            
                            {item.social_channels && Object.values(item.social_channels).some(v => v) && (
                                <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Social Media</p>
                                    <p className="font-medium text-sm">
                                        {Object.entries(item.social_channels).filter(([_, v]) => v).map(([k]) => k).join(', ')}
                                    </p>
                                </div>
                            )}
                            
                            <div className="p-2 rounded-lg bg-muted/50">
                                <p className="text-xs text-muted-foreground">Eingereicht am</p>
                                <p className="font-medium text-sm">
                                    {item.created_at ? format(parseISO(item.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : '-'}
                                </p>
                            </div>
                        </>
                    ) : item.type === 'shooting' ? (
                        <>
                            {/* Customer - prominent */}
                            {item.customer_name && (
                                <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
                                    <div className="flex items-center gap-3">
                                        <Building2 className="w-5 h-5 text-primary" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Kunde</p>
                                            <p className="font-bold text-primary">{item.customer_name}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Datum</p>
                                    <p className="font-medium">{format(parseISO(item.shooting_date), 'dd.MM.yyyy')}</p>
                                </div>
                                <div className="p-3 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Uhrzeit</p>
                                    <p className="font-medium">{item.start_time} - {item.end_time}</p>
                                </div>
                            </div>
                            
                            {item.team_members?.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium mb-2">Team vor Ort</p>
                                    <div className="space-y-1">
                                        {item.team_members.map((m, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm">
                                                <span>{m.first_name} {m.last_name}</span>
                                                {m.position && <Badge variant="secondary" className="text-xs">{m.position}</Badge>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {item.social_faces?.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium mb-2">Social Faces</p>
                                    <div className="space-y-1">
                                        {item.social_faces.map((f, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm">
                                                <span>{f.first_name} {f.last_name}</span>
                                                <span className="text-muted-foreground">{f.check_in_time} - {f.check_out_time}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {item.notes && (
                                <div>
                                    <p className="text-sm font-medium mb-2">Notizen</p>
                                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.notes}</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Datum</p>
                                    <p className="font-medium">{format(parseISO(item.expense_date), 'dd.MM.yyyy')}</p>
                                </div>
                                <div className="p-3 rounded-lg bg-muted/50">
                                    <p className="text-xs text-muted-foreground">Kategorie</p>
                                    <p className="font-medium">{item.category}</p>
                                </div>
                            </div>
                            
                            <div className="p-3 rounded-lg bg-primary/10 text-center">
                                <p className="text-xs text-muted-foreground mb-1">Betrag</p>
                                <p className="text-2xl font-bold text-green-600">{item.amount.toFixed(2)} €</p>
                            </div>
                            
                            <div className="p-3 rounded-lg bg-muted/50">
                                <p className="text-xs text-muted-foreground">Kunde</p>
                                <p className="font-medium">{item.customer_name || 'Nicht angegeben'}</p>
                            </div>
                            
                            {item.description && (
                                <div>
                                    <p className="text-sm font-medium mb-2">Beschreibung</p>
                                    <p className="text-sm text-muted-foreground">{item.description}</p>
                                </div>
                            )}
                            
                            {item.receipt_url && (
                                <div>
                                    <p className="text-sm font-medium mb-2">Beleg</p>
                                    <a
                                        href={item.receipt_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-sm text-blue-500 hover:underline"
                                    >
                                        <FileImage className="w-4 h-4" />
                                        Beleg anzeigen
                                    </a>
                                </div>
                            )}
                            
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                                <div>
                                    <p className="text-xs text-muted-foreground">Status</p>
                                    <Badge variant={
                                        item.status === 'approved' ? 'default' :
                                        item.status === 'rejected' ? 'destructive' : 'secondary'
                                    } className="mt-1">
                                        {item.status === 'approved' ? 'Genehmigt' :
                                         item.status === 'rejected' ? 'Abgelehnt' : 'Eingereicht'}
                                    </Badge>
                                </div>
                                {item.reviewed_by_name && (
                                    <div className="ml-auto text-right">
                                        <p className="text-xs text-muted-foreground">Bearbeitet von</p>
                                        <p className="text-sm font-medium">{item.reviewed_by_name}</p>
                                    </div>
                                )}
                            </div>
                            
                            {item.admin_comment && (
                                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                                    <p className="text-xs text-muted-foreground mb-1">Admin-Kommentar</p>
                                    <p className="text-sm">{item.admin_comment}</p>
                                </div>
                            )}
                            
                            {/* Admin Actions */}
                            {canApprove && item.status === 'submitted' && (
                                <div className="space-y-4 pt-5 border-t" data-testid="admin-actions">
                                    <div>
                                        <Label className="text-sm font-medium mb-1.5 block">Kommentar (optional)</Label>
                                        <Textarea
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            placeholder="Begründung oder Hinweis..."
                                            className="rounded-lg resize-none"
                                            rows={3}
                                        />
                                    </div>
                                    <div className="flex gap-3">
                                        <Button
                                            onClick={() => onStatusUpdate(item.id, 'approved', comment)}
                                            className="flex-1 gap-2 bg-green-600 hover:bg-green-700 h-11"
                                            data-testid="approve-btn"
                                        >
                                            <CheckCircle className="w-4 h-4" />
                                            Genehmigen
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            onClick={() => onStatusUpdate(item.id, 'rejected', comment)}
                                            className="flex-1 gap-2 h-11"
                                            data-testid="reject-btn"
                                        >
                                            <XCircle className="w-4 h-4" />
                                            Ablehnen
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    
                    <div className="text-xs text-muted-foreground pt-4 border-t">
                        Erstellt von {item.created_by_name} am {format(parseISO(item.created_at), 'dd.MM.yyyy HH:mm')}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default FormsPage;
